import { DISCOVERY_DOC_LABEL, DISCOVERY_FORM_PATH, LOB_NAME, STATUS, type Message, type SubmissionStatus } from '@commpass/shared';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { DownloadIcon, PlusIcon, SearchIcon } from '../components/Icons';
import { Messages } from '../components/Messages';
import { Modal } from '../components/Modal';
import { Pipeline } from '../components/Pipeline';
import { StatusPill } from '../components/StatusPill';
import { useToast } from '../components/Toast';
import { ApiError, api } from '../lib/api';
import { addRecent, getRecent, removeRecent, type RecentSub } from '../lib/recent';

interface Retrieved {
  id: string;
  status: string;
  data: Record<string, unknown>;
}

interface DocReq {
  id: string;
  label: string;
  note?: string;
  storagePath?: string;
  docName?: string;
}

// Chunked base64 so multi-MB uploads don't blow the call stack.
function toBase64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let bin = '';
  for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(bin);
}

// Plain-language status note shown to the agent on their tracking view.
const AGENT_NOTES: Record<string, string> = {
  received: "We've received your submission — it's in our queue.",
  review: 'Our team is reviewing the details.',
  quoting: "We're actively quoting this with carriers.",
  mga_creating: "We're preparing to submit this directly to an MGA or a standard carrier's underwriter.",
  mga_sent: "We've submitted this directly to an MGA or a standard carrier's underwriter.",
  info_needed: 'We need a bit more from you — see the messages or requested documents below.',
  proposal_sent: 'Your proposal is ready — download it below to review with your client.',
  bound: 'This account is bound. 🎉',
  declined: 'This submission has been closed.',
  closed_lost: 'This submission has been closed.',
};

// Insured/business fields an agent can correct after submitting. Mirrors the
// server-side whitelist in public.ts (submission-update).
const EDIT_FIELDS: { key: string; label: string; area?: boolean; placeholder?: string }[] = [
  { key: 'businessName', label: 'Business name', placeholder: 'Legal business name' },
  { key: 'primaryContact', label: 'Primary contact (owner / insurance contact)', placeholder: 'Contact name' },
  { key: 'phone', label: 'Insured phone', placeholder: '254-555-0123' },
  { key: 'email', label: 'Insured email', placeholder: 'contact@business.com' },
  { key: 'physicalAddress', label: 'Physical address', placeholder: 'Street, City, ST' },
  { key: 'mailingAddress', label: 'Mailing address (optional)', placeholder: 'If different' },
  { key: 'website', label: 'Website (optional)', placeholder: 'https://' },
  { key: 'fein', label: 'FEIN (optional)', placeholder: 'XX-XXXXXXX' },
  { key: 'yearsInBusiness', label: 'Years in business', placeholder: 'e.g. 6' },
  { key: 'descriptionOfOps', label: 'Description of operations', area: true },
  { key: 'knownLosses', label: 'Known losses (optional)', area: true },
];

export function AgentHome() {
  const navigate = useNavigate();
  const toast = useToast();
  const initialCode = new URLSearchParams(window.location.search).get('code') ?? '';
  const [showRetrieve, setShowRetrieve] = useState(Boolean(initialCode));
  const [code, setCode] = useState(initialCode);
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<Retrieved | null>(null);
  const [session, setSession] = useState<{ code: string; pin: string } | null>(null);
  const [msgBusy, setMsgBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState<Record<string, string>>({});
  const [editBusy, setEditBusy] = useState(false);
  const [recents, setRecents] = useState<RecentSub[]>(() => getRecent());
  // First-run welcome card (per-device). On error (e.g. private mode) default to
  // "already welcomed" so we never nag on every visit when we can't remember dismissal.
  const [welcomed, setWelcomed] = useState<boolean>(() => {
    try {
      return localStorage.getItem('commpass_welcomed') === '1';
    } catch {
      return true;
    }
  });
  const location = useLocation();

  // Land straight on the tracking view after a fresh submit (don't "disappear").
  useEffect(() => {
    const js = (location.state as { justSubmitted?: { code: string; pin: string } } | null)?.justSubmitted;
    if (!js) return;
    void (async () => {
      try {
        const r = await api.retrieveSubmission(js);
        setResult(r);
        setSession(js);
        addRecent({ code: r.id, businessName: r.data.businessName as string | undefined, status: r.status });
        setRecents(getRecent());
      } catch {
        /* ignore — they can still retrieve manually */
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function doRetrieve() {
    if (!code.trim() || pin.length < 4) {
      toast('Enter your code and PIN');
      return;
    }
    setBusy(true);
    try {
      const creds = { code: code.trim().toUpperCase(), pin };
      const r = await api.retrieveSubmission(creds);
      setResult(r);
      setSession(creds);
      setShowRetrieve(false);
      addRecent({ code: r.id, businessName: r.data.businessName as string | undefined, status: r.status });
      setRecents(getRecent());
    } catch (e) {
      toast(e instanceof ApiError ? e.message : 'Could not retrieve that submission');
    } finally {
      setBusy(false);
    }
  }

  async function sendAgentMessage(text: string) {
    if (!session) return;
    setMsgBusy(true);
    try {
      await api.postAgentMessage({ ...session, text });
      const refreshed = await api.retrieveSubmission(session);
      setResult(refreshed);
      toast('Message sent — Agents Alliance notified');
    } catch (e) {
      toast(e instanceof ApiError ? e.message : 'Could not send message');
    } finally {
      setMsgBusy(false);
    }
  }

  async function downloadProposal() {
    if (!session) return;
    try {
      const { url } = await api.downloadProposal(session);
      window.open(url, '_blank');
    } catch (e) {
      toast(e instanceof ApiError ? e.message : 'Could not download the proposal');
    }
  }

  async function uploadRequested(requestId: string, file: File) {
    if (!session) return;
    try {
      const data = toBase64(await file.arrayBuffer());
      await api.uploadRequestedDoc({ ...session, requestId, fileName: file.name, mime: file.type, data });
      const refreshed = await api.retrieveSubmission(session);
      setResult(refreshed);
      toast('Uploaded — thank you');
    } catch (e) {
      toast(e instanceof ApiError ? e.message : 'Upload failed');
    }
  }

  // Self-service: attach a document to this submission anytime (no staff request needed).
  async function addDocument(file: File) {
    if (!session) return;
    try {
      const data = toBase64(await file.arrayBuffer());
      await api.uploadSubmissionDoc({ ...session, fileName: file.name, mime: file.type, data });
      const refreshed = await api.retrieveSubmission(session);
      setResult(refreshed);
      toast('Uploaded — Agents Alliance can see it now.');
    } catch (e) {
      toast(e instanceof ApiError ? e.message : 'Upload failed');
    }
  }

  function openEdit() {
    if (!result) return;
    const d = result.data;
    setEditForm(Object.fromEntries(EDIT_FIELDS.map((f) => [f.key, (d[f.key] as string) || ''])));
    setEditing(true);
  }

  async function saveDetails() {
    if (!session) return;
    setEditBusy(true);
    try {
      const { changed } = await api.updateSubmission({ ...session, patch: editForm });
      const refreshed = await api.retrieveSubmission(session);
      setResult(refreshed);
      setEditing(false);
      toast(changed > 0 ? 'Details updated — Agents Alliance can see the changes now.' : 'No changes to save.');
    } catch (e) {
      toast(e instanceof ApiError ? e.message : 'Could not save your changes');
    } finally {
      setEditBusy(false);
    }
  }

  function openRecent(c: string) {
    setCode(c);
    setPin('');
    setShowRetrieve(true);
  }
  function forget(c: string) {
    removeRecent(c);
    setRecents(getRecent());
  }
  function dismissWelcome() {
    try {
      localStorage.setItem('commpass_welcomed', '1');
    } catch {
      /* ignore — best-effort */
    }
    setWelcomed(true);
  }

  return (
    <div className="wrap">
      <div className="page-head">
        <div>
          <div className="page-title">Submit a commercial risk to Agents Alliance</div>
          <div className="page-sub">
            Start a new submission, or pick up one you saved with your code and PIN.
          </div>
          <div className="tagline">Faster quotes. Less paperwork. Full transparency.</div>
        </div>
      </div>

      {welcomed ? (
        // Returning visitor: a quiet, persistent reminder that agents don't log in.
        <div
          style={{
            maxWidth: 960,
            margin: '0 0 20px',
            display: 'flex',
            gap: 10,
            alignItems: 'flex-start',
            background: 'var(--slate-soft)',
            border: '1px solid var(--border)',
            borderLeft: '3px solid var(--navy)',
            borderRadius: 8,
            padding: '12px 14px',
            fontSize: 13.5,
            lineHeight: 1.5,
            color: 'var(--text)',
          }}
        >
          <span>
            <b>No account or login needed.</b> To submit, just click <b>“Start a new submission”</b> below — you don’t
            sign in. The <b>“AAS staff sign in”</b> button at the top right is only for Agents Alliance staff.
          </span>
        </div>
      ) : (
        // First visit on this device: the 60-second orientation, dismissible.
        <div
          style={{
            position: 'relative',
            maxWidth: 960,
            margin: '0 0 20px',
            background: 'var(--card)',
            border: '1px solid var(--border)',
            borderTop: '3px solid var(--orange)',
            borderRadius: 'var(--r)',
            boxShadow: 'var(--shadow)',
            padding: '20px 22px',
          }}
        >
          <button
            onClick={dismissWelcome}
            aria-label="Dismiss"
            style={{ position: 'absolute', top: 10, right: 14, color: 'var(--muted)', fontSize: 22, lineHeight: 1, background: 'none', border: 'none', cursor: 'pointer' }}
          >
            ×
          </button>
          <div style={{ fontFamily: "'Archivo'", fontWeight: 800, fontSize: 18, color: 'var(--navy)', marginBottom: 3 }}>
            New to COMM-PASS? Here’s the quick version
          </div>
          <div className="sub-meta" style={{ marginBottom: 15 }}>About a minute — you won’t see this again on this device.</div>
          <div style={{ display: 'grid', gap: 12, marginBottom: 16 }}>
            {([
              ['No login or account needed.', 'Just click “Start a new submission” below and go.'],
              ['Save your code + 4-digit PIN.', 'When you submit you’ll get both — that’s how you check status or come back later.'],
              ['In a hurry? Save and finish later.', 'You can stop any time and pick up right where you left off.'],
            ] as [string, string][]).map(([h, b], i) => (
              <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                <div style={{ flex: 'none', width: 24, height: 24, borderRadius: '50%', background: 'var(--orange)', color: '#fff', fontWeight: 800, fontSize: 13, display: 'grid', placeItems: 'center', fontFamily: "'Archivo'" }}>
                  {i + 1}
                </div>
                <div style={{ fontSize: 14, lineHeight: 1.5 }}>
                  <b style={{ color: 'var(--navy)' }}>{h}</b> <span style={{ color: 'var(--muted)' }}>{b}</span>
                </div>
              </div>
            ))}
          </div>
          <button className="btn btn-accent" onClick={dismissWelcome}>Got it — let’s go</button>
        </div>
      )}

      <div className="card-grid" style={{ gap: 20, maxWidth: 960, gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
        <div
          className="sub-card"
          onClick={() => navigate('/new')}
          style={{ padding: '30px 28px', minHeight: 176, display: 'flex', flexDirection: 'column', justifyContent: 'center', cursor: 'pointer' }}
        >
          <div className="sub-card-top" style={{ marginBottom: 12 }}>
            <div className="sub-insured" style={{ fontSize: 21 }}>Start a new submission</div>
            <PlusIcon width={30} height={30} />
          </div>
          <div className="sub-meta" style={{ fontSize: 14, lineHeight: 1.55 }}>
            Walk through tier, agency, insured, and documents. Save any time and finish later.
          </div>
        </div>

        <div
          className="sub-card"
          onClick={() => setShowRetrieve(true)}
          style={{ padding: '30px 28px', minHeight: 176, display: 'flex', flexDirection: 'column', justifyContent: 'center', cursor: 'pointer' }}
        >
          <div className="sub-card-top" style={{ marginBottom: 12 }}>
            <div className="sub-insured" style={{ fontSize: 21 }}>Return to a saved submission</div>
            <SearchIcon width={30} height={30} />
          </div>
          <div className="sub-meta" style={{ fontSize: 14, lineHeight: 1.55 }}>
            Enter your submission code and 4-digit PIN to pick up where you left off.
          </div>
        </div>
      </div>

      {recents.length > 0 && !result && (
        <div style={{ maxWidth: 920, marginTop: 28 }}>
          <div className="sub-meta" style={{ marginBottom: 10, fontWeight: 600 }}>
            Your recent submissions (on this device)
          </div>
          <div style={{ display: 'grid', gap: 10 }}>
            {recents.map((r) => (
              <div
                key={r.code}
                className="sub-card"
                onClick={() => openRecent(r.code)}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '14px 18px', cursor: 'pointer' }}
              >
                <div>
                  <div className="sub-insured" style={{ fontSize: 15 }}>{r.businessName || 'Submission'}</div>
                  <div className="sub-meta">{r.code}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                  <StatusPill status={r.status || 'received'} />
                  <button
                    className="reset-link"
                    title="Forget on this device"
                    onClick={(e) => {
                      e.stopPropagation();
                      forget(r.code);
                    }}
                    style={{ color: 'var(--muted)', fontSize: 20, lineHeight: 1 }}
                  >
                    ×
                  </button>
                </div>
              </div>
            ))}
          </div>
          <div className="sub-meta" style={{ marginTop: 8, fontSize: 12 }}>
            Click one to reopen — you'll enter your PIN. Only the code is remembered here, never your PIN.
          </div>
        </div>
      )}

      {result && (
        <>
          <div className="back-link" style={{ marginTop: 20, cursor: 'pointer' }} onClick={() => setResult(null)}>
            ← Back to my submissions
          </div>
          <div className="panel" style={{ marginTop: 10 }}>
            <div className="panel-head">
              <h3>Submission {result.id}</h3>
              <StatusPill status={result.status} />
            </div>
            <div className="panel-pad">
              <div className="sub-insured" style={{ marginBottom: 4 }}>
                {(result.data.businessName as string) || 'Your submission'}
              </div>
              <div className="sub-meta" style={{ marginBottom: 14 }}>
                {(result.data.agencyName as string) || ''}
                {((result.data.lines as string[]) ?? []).length
                  ? ` · ${((result.data.lines as string[]) ?? []).map((l) => LOB_NAME[l] ?? l).join(', ')}`
                  : ''}
              </div>

              <div style={{ background: 'var(--slate-soft)', borderLeft: '3px solid var(--navy)', borderRadius: 8, padding: '10px 12px', fontSize: 13.5, marginBottom: 16 }}>
                {AGENT_NOTES[result.status] ?? "We're on it — we'll keep you posted right here."}
              </div>

              {result.status !== 'draft' && <Pipeline status={result.status} />}

              {result.data.proposalDoc != null && (
                <button
                  className="btn btn-accent"
                  style={{ marginTop: 16 }}
                  onClick={() => void downloadProposal()}
                >
                  Download your proposal
                </button>
              )}
              {result.status === 'draft' && (
                <button
                  className="btn btn-accent"
                  style={{ marginTop: 16 }}
                  onClick={() => navigate('/new', { state: { resume: result } })}
                >
                  Resume this submission
                </button>
              )}
            </div>
          </div>

          {result.status !== 'draft' && (
            <div className="panel" style={{ marginTop: 22 }}>
              <div className="panel-head">
                <h3>Account details</h3>
                <button className="btn btn-ghost btn-sm" onClick={openEdit}>
                  Edit details
                </button>
              </div>
              <div className="panel-pad">
                <div className="sub-meta" style={{ marginBottom: 12 }}>
                  Keep the business name, contact, phone, email, and address accurate so we can quote and reach your
                  insured. You can update these anytime.
                </div>
                {EDIT_FIELDS.map((f) => {
                  const v = (result.data[f.key] as string) || '';
                  return (
                    <div
                      key={f.key}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: 16,
                        padding: '7px 0',
                        borderBottom: '1px solid var(--border)',
                      }}
                    >
                      <span className="sub-meta" style={{ flex: 'none' }}>
                        {f.label}
                      </span>
                      <span style={{ textAlign: 'right', color: v ? 'var(--text)' : 'var(--muted)' }}>
                        {v || '—'}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {Array.isArray(result.data.docRequests) && (result.data.docRequests as DocReq[]).length > 0 && (
            <div className="panel" style={{ marginTop: 22 }}>
              <div className="panel-head">
                <h3>Requested documents</h3>
              </div>
              <div className="panel-pad">
                <div className="sub-meta" style={{ marginBottom: 10 }}>
                  Agents Alliance has asked you to upload these:
                </div>
                {(result.data.docRequests as DocReq[]).map((r) => (
                  <div
                    key={r.id}
                    style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--border)' }}
                  >
                    <div>
                      <b>{r.label}</b>
                      {r.note && <div className="sub-meta">{r.note}</div>}
                      {r.label === DISCOVERY_DOC_LABEL && !r.storagePath && (
                        <a
                          className="doc-getlink"
                          href={DISCOVERY_FORM_PATH}
                          download="Discovery - Commercial Lines New Business.xlsx"
                          style={{ marginTop: 4 }}
                        >
                          <DownloadIcon width={13} height={13} /> Get the blank Discovery form to fill out
                        </a>
                      )}
                    </div>
                    {r.storagePath ? (
                      <span className="sub-meta">✓ Uploaded</span>
                    ) : (
                      <label className="btn btn-accent btn-sm" style={{ cursor: 'pointer' }}>
                        Upload
                        <input
                          type="file"
                          hidden
                          onChange={(e) => {
                            const f = e.target.files?.[0];
                            if (f) void uploadRequested(r.id, f);
                            e.target.value = '';
                          }}
                        />
                      </label>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {result.status !== 'draft' && (
            <div className="panel" style={{ marginTop: 22 }}>
              <div className="panel-head">
                <h3>Your documents</h3>
              </div>
              <div className="panel-pad">
                <div className="sub-meta" style={{ marginBottom: 10 }}>
                  Attach anything that helps us quote — ACORDs, a completed Discovery / subsheet, loss runs, or dec
                  pages. You can add documents here anytime.
                </div>
                {(() => {
                  const docs = (Array.isArray(result.data.documents) ? result.data.documents : []) as {
                    label?: string;
                    fileName?: string;
                    storagePath?: string;
                  }[];
                  const attached = docs.filter((x) => x.storagePath);
                  return attached.length === 0 ? (
                    <div className="sub-meta">No documents attached yet.</div>
                  ) : (
                    attached.map((doc, i) => (
                      <div
                        key={i}
                        style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--border)' }}
                      >
                        <b>{doc.label || doc.fileName || 'Document'}</b>
                        <span className="sub-meta">✓ Attached</span>
                      </div>
                    ))
                  );
                })()}
                <label className="btn btn-accent btn-sm" style={{ marginTop: 12, cursor: 'pointer' }}>
                  <PlusIcon width={15} height={15} /> Add a document
                  <input
                    type="file"
                    hidden
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) void addDocument(f);
                      e.target.value = '';
                    }}
                  />
                </label>
              </div>
            </div>
          )}

          <Messages
            messages={(result.data.messages as Message[] | undefined) ?? []}
            me="agent"
            onSend={sendAgentMessage}
            busy={msgBusy}
          />

          {Array.isArray(result.data.statusHistory) &&
            (result.data.statusHistory as { status: string; at: number }[]).length > 0 && (
              <div className="panel" style={{ marginTop: 22 }}>
                <div className="panel-head">
                  <h3>Timeline</h3>
                </div>
                <div className="panel-pad">
                  {[...(result.data.statusHistory as { status: string; at: number }[])].reverse().map((h, i) => (
                    <div key={i} className="tl-item">
                      <div className="tl-dot" />
                      <div>
                        <b>{STATUS[h.status as SubmissionStatus]?.label ?? h.status}</b>
                        <div className="sub-meta">{new Date(h.at).toLocaleString()}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
        </>
      )}

      {editing && (
        <Modal
          title="Edit account details"
          subtitle="Correct anything here — it updates your submission and lets Agents Alliance know."
          onClose={() => setEditing(false)}
          footer={
            <>
              <button className="btn btn-ghost" onClick={() => setEditing(false)}>
                Cancel
              </button>
              <button className="btn btn-primary" disabled={editBusy} onClick={() => void saveDetails()}>
                {editBusy ? 'Saving…' : 'Save changes'}
              </button>
            </>
          }
        >
          <div style={{ display: 'grid', gap: 12 }}>
            {EDIT_FIELDS.map((f) => (
              <div key={f.key} className="fgroup" style={{ margin: 0 }}>
                <label className="flabel">{f.label}</label>
                {f.area ? (
                  <textarea
                    className="finput"
                    rows={3}
                    placeholder={f.placeholder}
                    value={editForm[f.key] ?? ''}
                    onChange={(e) => setEditForm((s) => ({ ...s, [f.key]: e.target.value }))}
                  />
                ) : (
                  <input
                    className="finput"
                    placeholder={f.placeholder}
                    value={editForm[f.key] ?? ''}
                    onChange={(e) => setEditForm((s) => ({ ...s, [f.key]: e.target.value }))}
                  />
                )}
              </div>
            ))}
          </div>
        </Modal>
      )}

      {showRetrieve && (
        <Modal
          title="Reopen a submission"
          subtitle="Enter your submission code and PIN to pull it up on this device."
          onClose={() => setShowRetrieve(false)}
          footer={
            <>
              <button className="btn btn-ghost" onClick={() => setShowRetrieve(false)}>
                Cancel
              </button>
              <button className="btn btn-primary" disabled={busy} onClick={() => void doRetrieve()}>
                {busy ? 'Looking…' : 'Find it'}
              </button>
            </>
          }
        >
          <div className="fgroup">
            <label className="flabel">Submission code</label>
            <input
              className="finput"
              placeholder="CP-XXXXXX"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              style={{ maxWidth: 220, textTransform: 'uppercase' }}
            />
          </div>
          <div className="fgroup">
            <label className="flabel">PIN</label>
            <input
              className="finput"
              inputMode="numeric"
              maxLength={4}
              placeholder="4-digit PIN"
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/[^0-9]/g, '').slice(0, 4))}
              style={{ maxWidth: 160, letterSpacing: 6, fontWeight: 800, fontSize: 18, textAlign: 'center' }}
            />
          </div>
        </Modal>
      )}
    </div>
  );
}
